import { useState } from 'react';
import Swal from 'sweetalert2';
import WeddingStepLogIn from 'src/containers/wedding-request/newRequest/steps/StepLogIn';
import queryString from 'query-string';
import StepChecks from 'src/containers/wedding-request/newRequest/steps/StepChecks';
import { WeddingRequestComponent } from 'src/containers/wedding-request/newRequest/weddingRequest-styles';
import StepNotfulfilled from 'src/containers/wedding-request/newRequest/steps/StepNotfulfilled';
import StepReadDoc from 'src/containers/wedding-request/newRequest/steps/StepReadDoc';
import StepFillPersonData from 'src/containers/wedding-request/newRequest/steps/StepFillPersonData';
import StepFillCoupleData from 'src/containers/wedding-request/newRequest/steps/StepFillCoupleData';
import { emptyWeddingFormPersonAnswers } from 'src/containers/wedding-request/newRequest/questions/weddingFormPersonQuestions';
import StepProcessing from 'src/containers/wedding-request/newRequest/steps/StepProcessing';
import weddingRequestService from 'src/services/api/weddingRequestService';
import { useEffect } from 'react';
import { scrollUp } from 'src/components/helpers';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';

const preSetMainGuest = (mainGuest) => {
  if (!mainGuest) return {};
  const personData = { ...emptyWeddingFormPersonAnswers, ...mainGuest };
  if (mainGuest.gender === 'Masculino') {
    return { male: personData };
  }
  return { female: personData };
};

const WeddingRequest = () => {
  const [inputs, setInputs] = useState({
    male: emptyWeddingFormPersonAnswers,
    female: emptyWeddingFormPersonAnswers,
  });
  const [step, setStep] = useState('logIn');
  const [loading, set_loading] = useState(false);
  const [weddingRequestProcess, set_weddingRequestProcess] = useState({});
  const [image, set_image] = useState(null);
  const getCaptchaToken = useRecaptchaToken('wedding_submit');
  const captchaRequired = isCaptchaRequired();

  useEffect(() => {
    scrollUp();
  }, [step]);

  let query = window.location.search;
  let parsedQueryString = queryString.parse(query);

  useEffect(() => {
    const identification = parsedQueryString.identification?.trim();
    const phone = parsedQueryString.phone?.trim();
    if (identification || phone) {
      setInputs((prev) => ({
        ...prev,
        ...(identification && { identification }),
        ...(phone && { phone }),
      }));
    }
  }, []);

  const handleInputs = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    set_loading(true);

    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      set_loading(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    const payload = {
      ...inputs,
      coupleQuestions: {
        ...(inputs.coupleQuestions || {}),
        captcha,
      },
    };

    const data = new FormData();
    data.append('couplePicture', image ? image : null);
    Object.entries(payload).forEach(([key, value]) => data.append(key, JSON.stringify(value)));

    weddingRequestService
      .submitWeddingRequest(data)
      .then((response) => {
        set_loading(false);
        const { newWeddingRequest, success } = response.data;
        if (success && newWeddingRequest?.id) {
          set_weddingRequestProcess(newWeddingRequest);
          setStep('step-processing');
        }
        if (response?.data?.message) Swal.fire(response?.data?.message);
      })
      .catch((error) => {
        set_loading(false);
        Swal.fire(error.response?.data?.message || 'Ha ocurrido un error al enviar la solicitud');
      });
  };

  function getInitialInfo(loginInputs = inputs) {
    set_loading(true);
    const { male, female, ...loginFields } = loginInputs;
    const body = { ...parsedQueryString, ...loginFields };
    delete body.ac;
    weddingRequestService
      .getInitialInfo(body)
      .then((response) => {
        set_loading(false);
        const { mainGuest, weddingRequestProcess, success, flags, message } = response.data;
        if (!success) return;

        if (weddingRequestProcess) {
          setStep('step-processing');
          set_weddingRequestProcess(weddingRequestProcess);
          return;
        }

        if (!mainGuest) {
          if (message) Swal.fire(message);
          return;
        }

        setStep('checks');
        setInputs((prev) => ({
          ...prev,
          identification: loginFields.identification,
          phone: loginFields.phone,
          ...preSetMainGuest(mainGuest),
          flags,
        }));
        if (message) Swal.fire(message);
      })
      .catch((error) => {
        if (error.response) Swal.fire(error.response.data.message);
        set_loading(false);
      });
  }

  const renderStep = () => {
    switch (step) {
      case 'logIn':
        return (
          <WeddingStepLogIn
            inputs={inputs}
            setInputs={setInputs}
            getInfo={getInitialInfo}
            parsedQueryString={parsedQueryString}
            set_loading={set_loading}
            loading={loading}
            handleInputs={handleInputs}
          />
        );
      case 'checks':
        return (
          <StepChecks
            inputs={inputs}
            weddingRequestProcess={weddingRequestProcess}
            setStep={setStep}
          />
        );
      case 'not-fulfilled':
        return <StepNotfulfilled />;
      case 'step-read-doc':
        return <StepReadDoc setStep={setStep} inputs={inputs} setInputs={setInputs} />;
      case 'step-fill-his-data':
        return (
          <StepFillPersonData
            person={{ name: 'Él', gender: 'male' }}
            directions={{ next: 'step-fill-her-data', prev: 'step-read-doc' }}
            setStep={setStep}
            inputs={inputs}
            setInputs={setInputs}
            step={step}
          />
        );
      case 'step-fill-her-data':
        return (
          <StepFillPersonData
            person={{ name: 'Ella', gender: 'female' }}
            directions={{ next: 'step-fill-their-data', prev: 'step-fill-his-data' }}
            setStep={setStep}
            inputs={inputs}
            setInputs={setInputs}
            step={step}
          />
        );
      case 'step-fill-their-data':
        return (
          <StepFillCoupleData
            directions={{ next: 'step-processing', prev: 'step-fill-his-data' }}
            setStep={setStep}
            inputs={inputs}
            handleSubmit={handleSubmit}
            loading={loading}
            setInputs={setInputs}
            set_image={set_image}
            image={image}
          />
        );
      case 'step-processing':
        return <StepProcessing weddingRequestProcess={weddingRequestProcess} />;
      default:
        return null;
    }
  };

  return (
    <WeddingRequestComponent>
      <h1 style={{ textAlign: 'center' }}>Solicitud de Matrimonio</h1>
      {renderStep()}
    </WeddingRequestComponent>
  );
};

export default WeddingRequest;
